import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import ProcedureRunner from '@/components/ProcedureRunner';
import RecommendedTestPopup from '@/components/RecommendedTestPopup';
import DiagnosticTabBar from '@/components/DiagnosticTabBar';
import { toast } from 'sonner';
import { LANG_LABELS } from '@/i18n';
import { useAuth } from '@/lib/auth';
import { useSessions } from '@/lib/sessions';
import { Send, Bot, User as UserIcon, AlertTriangle, FlaskConical, BadgeCheck, Sparkles, FileText, CheckCircle2, Target, MessageSquare, Plus } from 'lucide-react';

export default function DiagnosticChatPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const lang = i18n.language || user?.language || 'it';
  const { activeChat, scheduleUpdate, createSession } = useSessions();
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [provider, setProvider] = useState('anthropic');
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  // The most recent set of recommended universal procedures from the AI response,
  // surfaced via the RecommendedTestPopup. Cleared/changed only when a new response
  // brings a fresh set (or empty array if none).
  const [recommended, setRecommended] = useState([]);
  const endRef = useRef();
  // Track which language the current chat history was generated in.
  const langRef = useRef(lang);
  const lastQuestionRef = useRef('');
  // Hydration guard: when we load state from the active session into useState we
  // don't want those setX calls to immediately fire a save back. The ref is set to
  // true during hydration and cleared on the next tick.
  const hydratingRef = useRef(false);

  // Hydrate local state from the active session whenever the user switches tab.
  // This is the ONLY place that should overwrite from the session.
  useEffect(() => {
    hydratingRef.current = true;
    if (!activeChat) {
      setBrand(''); setModel(''); setMessages([]);
      setSessionId(null); setProvider('anthropic'); setQuestion('');
      setRecommended([]);
      lastQuestionRef.current = '';
    } else {
      setBrand(activeChat.brand || '');
      setModel(activeChat.model || '');
      const s = activeChat.state || {};
      setMessages(s.messages || []);
      setSessionId(s.session_id || null);
      setProvider(s.provider || 'anthropic');
      setQuestion(s.question_draft || '');
      setRecommended(s.recommended || []);
      lastQuestionRef.current = s.last_question || '';
    }
    // Release the guard after the state has settled
    const tid = setTimeout(() => { hydratingRef.current = false; }, 0);
    return () => clearTimeout(tid);
  }, [activeChat?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Persist any local change back to the active session (debounced inside the provider).
  useEffect(() => {
    if (!activeChat || hydratingRef.current) return;
    scheduleUpdate(activeChat.id, {
      brand, model,
      state: {
        messages,
        session_id: sessionId,
        provider,
        question_draft: question,
        recommended,
        last_question: lastQuestionRef.current,
      },
    });
  }, [activeChat?.id, brand, model, messages, sessionId, provider, question, recommended, scheduleUpdate]);

  const fetchBrands = useCallback(() => {
    api.get('/brands').then((r) => setBrands(r.data));
  }, []);
  useEffect(() => { fetchBrands(); }, [fetchBrands]);
  useEffect(() => {
    if (brand) api.get(`/brands/${encodeURIComponent(brand)}/models`).then((r) => setModels(r.data));
    else setModels([]);
    setModel('');
  }, [brand]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading]);

  const askQuestion = async (q, opts = {}) => {
    if (!brand || !model || !q.trim()) return;
    const useSession = opts.newSession ? null : sessionId;
    if (!opts.silent) {
      setMessages((m) => [...m, { id: `u-${Date.now()}-${Math.random().toString(36).slice(2,7)}`, role: 'user', text: q, lang }]);
    }
    lastQuestionRef.current = q;
    setLoading(true);
    try {
      // Single backend call: the diagnostic engine returns BOTH the AI answer and
      // the prioritized cross-check (same engine used by Guided Diagnosis).
      const { data } = await api.post('/chat/diagnostic', {
        brand, model, question: q, provider, session_id: useSession, language: lang,
      });
      const cross = data.cross_check;
      if (cross && (cross.controls || []).length > 0) {
        setMessages((m) => [...m, {
          id: `cc-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          role: 'controls',
          controls: cross.controls,
          universal_procedures: data.universal_procedures || [],
          universal_categories: data.universal_categories || [],
          has_resolved: !!cross.has_resolved_field_report,
          lang,
        }]);
      } else if ((data.universal_procedures || []).length > 0) {
        // No OEM cross-check hits but the classifier found applicable universal tests
        setMessages((m) => [...m, {
          id: `up-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          role: 'controls',
          controls: [],
          universal_procedures: data.universal_procedures,
          universal_categories: data.universal_categories || [],
          has_resolved: false,
          lang,
        }]);
      }
      setSessionId(data.session_id);
      setRecommended(data.universal_procedures || []);
      setMessages((m) => [...m, {
        id: `a-${Date.now()}-${Math.random().toString(36).slice(2,7)}`,
        role: 'assistant', text: data.answer,
        system: data.system_identified, system_label: data.system_label,
        match_specificity: data.match_specificity, alarm: data.alarm_matched,
        lang,
      }]);
    } catch (err) {
      setMessages((m) => [...m, {
        id: `e-${Date.now()}-${Math.random().toString(36).slice(2,7)}`,
        role: 'error', text: err?.response?.data?.detail || 'AI Error', lang,
      }]);
    } finally { setLoading(false); }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!brand || !model || !question.trim()) {
      toast.error(t('app.required'));
      return;
    }
    const q = question.trim();
    setQuestion('');
    await askQuestion(q);
  };

  // Real-time language sync: when language changes and there is at least one
  // assistant response, re-issue the last question in the new language so that
  // the user always sees a fresh, language-coherent diagnosis.
  useEffect(() => {
    if (langRef.current === lang) return;
    langRef.current = lang;
    if (lastQuestionRef.current && !loading) {
      // Notify and regenerate; reset session so the LLM doesn't carry old-lang context.
      toast.info(t('chat.language_hint', { lang: LANG_LABELS[lang]?.native || lang }));
      setSessionId(null);
      // Replace messages with the same user question + new assistant reply in target lang.
      setMessages([{
        id: `u-relang-${Date.now()}`, role: 'user', text: lastQuestionRef.current, lang,
      }]);
      askQuestion(lastQuestionRef.current, { newSession: true, silent: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  return (
    <div className="animate-fade-up h-[calc(100vh-7rem)] flex flex-col" data-testid="chat-page">
      <DiagnosticTabBar kind="chat" />

      {!activeChat ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="surface p-10 text-center max-w-md" data-testid="chat-empty-state">
            <MessageSquare className="w-12 h-12 text-gray-300 mx-auto" strokeWidth={1.2} />
            <h2 className="font-display font-bold text-xl mt-4">{t('chat.title')}</h2>
            <p className="text-sm text-gray-500 mt-2">
              {t('sessions.empty_state_chat') || 'Nessuna sessione attiva. Crea la tua prima sessione per iniziare.'}
            </p>
            <button
              onClick={() => createSession('chat')}
              className="btn-primary mt-5 mx-auto"
              data-testid="chat-create-first"
            >
              <Plus className="w-4 h-4" /> {t('sessions.start_now') || 'Avvia ora'}
            </button>
          </div>
        </div>
      ) : (
        <>
      <div className="flex items-end justify-between flex-wrap gap-2 mt-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="overline text-[#002FA7]">{t('chat.title').toUpperCase()}</div>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-800 text-[10px] uppercase tracking-wider font-mono">
              {t('chat.advanced_tag')}
            </span>
            <span className="text-[10px] font-mono uppercase text-green-700 ml-1" data-testid="chat-autosave-indicator">
              · {t('sessions.autosave') || 'salvataggio automatico'}
            </span>
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tight mt-1">{activeChat.title || t('chat.title')}</h1>
          <p className="text-xs text-gray-500 mt-1 font-mono">{t('chat.language_hint', { lang: LANG_LABELS[lang]?.native || lang })}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-6">
        <select value={brand} onChange={(e) => setBrand(e.target.value)} className="input-base" data-testid="chat-brand">
          <option value="">— {t('chat.select_brand')} —</option>
          {brands.map((b) => <option key={b}>{b}</option>)}
        </select>
        <select value={model} onChange={(e) => setModel(e.target.value)} disabled={!brand} className="input-base" data-testid="chat-model">
          <option value="">— {t('chat.select_model')} —</option>
          {models.map((m) => <option key={m}>{m}</option>)}
        </select>
        <select value={provider} onChange={(e) => setProvider(e.target.value)} className="input-base" data-testid="chat-provider">
          <option value="anthropic">Claude Sonnet 4.5</option>
          <option value="openai">GPT-5.2</option>
        </select>
      </div>

      <div className="surface mt-4 flex-1 overflow-y-auto p-4 sm:p-6 space-y-4" data-testid="chat-messages">
        {messages.length === 0 && !loading && (
          <div className="text-center py-12">
            <Bot className="w-10 h-10 text-gray-300 mx-auto" strokeWidth={1.2} />
            <div className="font-display font-bold mt-3 text-lg">{t('chat.title')}</div>
            <p className="text-sm text-gray-500 mt-1 max-w-md mx-auto">{t('chat.subtitle')}</p>
          </div>
        )}
        {messages.map((m, i) => {
          if (m.role === 'controls') {
            const ups = m.universal_procedures || [];
            return (
              <div key={m.id || `msg-${i}`} className="border-l-4 border-amber-500 bg-amber-50/40 p-3 sm:p-4" data-testid={`chat-controls-${i}`}>
                {m.controls.length > 0 && (
                  <>
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <FlaskConical className="w-4 h-4 text-amber-700" />
                      <span className="font-display font-bold text-amber-900 text-sm">Controlli prioritizzati</span>
                      <span className="text-xs text-amber-700 font-mono">({m.controls.length})</span>
                      {m.has_resolved && (
                        <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 px-2 py-0.5 text-[10px] font-mono uppercase ml-auto">
                          <CheckCircle2 className="w-3 h-3" /> caso reale risolto
                        </span>
                      )}
                    </div>
                    <ol className="space-y-1.5">
                      {m.controls.slice(0, 5).map((c, idx) => {
                        const isFR = c.source === 'field_report';
                        const isPP = c.source === 'procedure_pattern';
                        const Icon = isFR ? BadgeCheck : isPP ? Sparkles : FileText;
                        const sourceLabel = isFR ? 'Field Report' : isPP ? 'Procedure Pattern' : 'Lista Allarmi';
                        const badgeClass = isFR ? 'bg-green-100 text-green-900' : isPP ? 'bg-blue-100 text-blue-900' : 'bg-gray-100 text-gray-800';
                        return (
                          <li key={`${c.source}-${c.id || idx}`} className="bg-white border border-amber-200 p-2 text-xs flex items-start gap-2">
                            <span className="w-5 h-5 bg-amber-600 text-white flex items-center justify-center text-[10px] font-mono font-bold shrink-0">{idx + 1}</span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className={`inline-flex items-center gap-0.5 px-1 py-0.5 text-[9px] font-mono uppercase ${badgeClass}`}>
                                  <Icon className="w-2.5 h-2.5" />{sourceLabel}
                                </span>
                                {c.error_code && <span className="bg-red-100 text-red-800 px-1.5 py-0 text-[10px] font-mono font-bold">{c.error_code}</span>}
                                {isFR && c.result === 'risolto' && <span className="text-green-700 text-[9px] font-mono uppercase">risolto</span>}
                                {isPP && c.success_count > 0 && <span className="inline-flex items-center gap-0.5 text-green-700 text-[10px] font-mono"><Target className="w-2.5 h-2.5" />{c.success_count}</span>}
                              </div>
                              <div className="font-semibold text-gray-900 mt-0.5 truncate">{c.title || '—'}</div>
                              {c.root_cause && <div className="text-[10px] text-gray-600 mt-0.5 line-clamp-2">→ {c.root_cause}</div>}
                            </div>
                          </li>
                        );
                      })}
                    </ol>
                  </>
                )}
                {ups.length > 0 && (
                  <div className={m.controls.length > 0 ? 'mt-3 pt-3 border-t border-amber-200' : ''}>
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <FlaskConical className="w-4 h-4 text-indigo-700" />
                      <span className="font-display font-bold text-indigo-900 text-sm">Procedure di test universali</span>
                      <span className="text-xs text-indigo-700 font-mono">({ups.length})</span>
                      {m.universal_categories?.length > 0 && (
                        <span className="text-[10px] text-indigo-700 font-mono uppercase ml-1">
                          [{m.universal_categories.join(' · ')}]
                        </span>
                      )}
                    </div>
                    <ol className="space-y-1.5">
                      {ups.map((p) => {
                        const title = (p.title || {})[m.lang] || (p.title || {}).it || (p.title || {}).en || p.category;
                        const steps = (p.steps || []).slice(0, 4);
                        return (
                          <li key={p.id} className="bg-white border border-indigo-200 p-2 text-xs">
                            <div className="flex items-start justify-between gap-2">
                              <div className="font-semibold text-indigo-900 flex-1">[{p.category}] {title}</div>
                              <ProcedureRunner procedure={p} lang={m.lang} variant="compact" context={{ brand, model, question: lastQuestionRef.current }} />
                            </div>
                            {steps.length > 0 && (
                              <ol className="mt-1 space-y-0.5">
                                {steps.map((s) => (
                                  <li key={s.order} className="text-[11px] text-gray-700">
                                    <span className="font-mono text-indigo-700">{s.order}.</span> {s[m.lang] || s.it || s.en}
                                    {s.expected_value && <span className="text-green-700 font-mono ml-1">[{s.expected_value}]</span>}
                                  </li>
                                ))}
                              </ol>
                            )}
                          </li>
                        );
                      })}
                    </ol>
                  </div>
                )}
              </div>
            );
          }
          return (
          <div key={m.id || `msg-${i}`} className={`flex gap-3 ${m.role === 'user' ? 'justify-end' : ''}`}>
            {m.role !== 'user' && (
              <div className={`w-8 h-8 flex items-center justify-center shrink-0 ${m.role === 'error' ? 'bg-red-100 text-red-700' : 'bg-[#002FA7] text-white'}`}>
                {m.role === 'error' ? <AlertTriangle className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
              </div>
            )}
            <div className={`max-w-[85%] sm:max-w-[70%] ${m.role === 'user' ? 'order-1' : ''}`}>
              <div className={`p-3 sm:p-4 border ${
                m.role === 'user' ? 'bg-[#002FA7] text-white border-[#002FA7]'
                : m.role === 'error' ? 'bg-red-50 border-red-200 text-red-900'
                : 'bg-white border-gray-200'
              }`}>
                <div className="whitespace-pre-wrap text-sm leading-relaxed">{m.text}</div>
              </div>
            </div>
            {m.role === 'user' && (
              <div className="w-8 h-8 bg-gray-200 text-gray-700 flex items-center justify-center shrink-0"><UserIcon className="w-4 h-4" /></div>
            )}
          </div>
          );
        })}
        {loading && (
          <div className="flex gap-3">
            <div className="w-8 h-8 bg-[#002FA7] text-white flex items-center justify-center"><Bot className="w-4 h-4" /></div>
            <div className="bg-white border border-gray-200 p-3 text-sm text-gray-500">{t('chat.thinking')}</div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <form onSubmit={send} className="mt-3 flex gap-2" data-testid="chat-form">
        <input className="input-base flex-1" placeholder={t('chat.ask_placeholder')}
          value={question} onChange={(e) => setQuestion(e.target.value)} data-testid="chat-input" />
        <button type="submit" className="btn-primary" disabled={loading} data-testid="chat-send">
          <Send className="w-4 h-4" /> {t('chat.send')}
        </button>
      </form>

      {/* Recommended universal test pop-up: opens once per AI response that suggests
          one or more universal procedures, so the technician can jump straight into
          the runner without scrolling the chat. Dismissed-per-signature. */}
      <RecommendedTestPopup
        procedures={recommended}
        context={{ brand, model, question: lastQuestionRef.current }}
        lang={lang}
      />
        </>
      )}
    </div>
  );
}
